// Navigation labels
export const navigationLabels = {
  dashboard: 'Tablou de bord',
  patients: 'Pacienți',
  diagnoses: 'Diagnostice',
  procedures: 'Proceduri',
  users: 'Utilizatori',
  reports: 'Rapoarte',
  settings: 'Setări',
  logout: 'Deconectare',
  profile: 'Profil',
  search: 'Căutare',
  export: 'Export',
  import: 'Import',
  help: 'Ajutor',
} as const;

// Form labels
export const formLabels = {
  // Common
  name: 'Nume',
  save: 'Salvează',
  cancel: 'Anulează',
  edit: 'Editează',
  delete: 'Șterge',
  add: 'Adaugă',
  create: 'Creează',
  update: 'Actualizează',
  view: 'Vizualizare',
  close: 'Închide',
  confirm: 'Confirmă',
  back: 'Înapoi',
  next: 'Următorul',
  previous: 'Anterior',
  loading: 'Se încarcă...',
  submit: 'Trimite',
  reset: 'Resetează',

  // Patient fields
  patientCode: 'Cod pacient',
  birthDate: 'Data nașterii',
  gender: 'Sex',
  birthWeight: 'Greutate la naștere (g)',
  gestationalAge: 'Vârstă gestațională (săpt.)',
  hospital: 'Spital',
  
  // Gender options
  female: 'Feminin',
  male: 'Masculin',

  // Auth fields
  username: 'Nume utilizator',
  password: 'Parolă',
  email: 'Email',
  login: 'Autentificare',
  rememberMe: 'Ține-mă minte',
  forgotPassword: 'Ai uitat parola?',
  currentPassword: 'Parola curentă',
  newPassword: 'Parola nouă',
  confirmPassword: 'Confirmă parola',
  changePassword: 'Schimbă parola',
  sendResetInstructions: 'Trimite instrucțiuni de resetare',
  resetInstructionsSent: 'Instrucțiuni de resetare trimise',
  resetInstructionsSentDescription: 'Verificați-vă emailul pentru instrucțiuni de resetare a parolei.',
  backToLogin: 'Înapoi la autentificare',
  resetPassword: 'Resetează parola',
  resetPasswordTitle: 'Resetare parolă',
  resetPasswordDescription: 'Introduceți o parolă nouă și puternică',
  passwordResetSuccess: 'Parola a fost resetată cu succes!',
  passwordResetSuccessDescription: 'Vă rugăm să vă autentificați cu noua parolă.',

  // Diagnosis fields
  icd10Code: 'Cod ICD-10',
  description: 'Descriere',
  isPrimary: 'Diagnostic primar',
  severityLevel: 'Nivel severitate',
  diagnosisDate: 'Data diagnosticului',
  notes: 'Notițe',
  
  // Procedure fields
  procedureType: 'Tip procedură',
  procedureName: 'Denumire procedură',
  procedureDate: 'Data procedură',
  outcome30d: 'Rezultat 30 zile',
  complications: 'Complicații',
  reintervention1y: 'Reintervenție 1 an',
  performedBy: 'Executat de',
  addProcedure: 'Adaugă procedură nouă',
  
} as const;

// Status messages
export const statusMessages = {
  success: 'Operația a fost realizată cu succes',
  error: 'A apărut o eroare',
  warning: 'Atenție',
  info: 'Informație',
  confirmation: 'Confirmați acțiunea',
  
  // Specific operations
  saved: 'Salvat cu succes',
  deleted: 'Șters cu succes',
  updated: 'Actualizat cu succes',
  created: 'Creat cu succes',
  
  // Loading states
  loading: 'Se încarcă...',
  saving: 'Se salvează...',
  deleting: 'Se șterge...',
  updating: 'Se actualizează...',
  processing: 'Se procesează...',
  sending: 'Se trimite...',
  resetting: 'Se resetează...',
  checking: 'Se verifică...',
} as const;

// Error messages
export const errorMessages = {
  required: 'Acest câmp este obligatoriu',
  invalidFormat: 'Format invalid',
  invalidDate: 'Data introdusă este invalidă',
  invalidEmail: 'Adresa de email este invalidă',
  passwordTooShort: 'Parola trebuie să aibă cel puțin 8 caractere',
  passwordMismatch: 'Parolele nu se potrivesc',
  
  // Network errors
  networkError: 'Eroare de rețea. Vă rugăm încercați din nou.',
  serverError: 'Eroare de server. Contactați administratorul.',
  unauthorized: 'Nu aveți permisiunea să accesați această resursă',
  sessionExpired: 'Sesiunea a expirat. Vă rugăm să vă autentificați din nou.',
  notFound: 'Resursa nu a fost găsită',
  
  // Validation errors
  minLength: (min: number) => `Trebuie să aibă cel puțin ${min} caractere`,
  maxLength: (max: number) => `Nu poate depăși ${max} caractere`,
  minValue: (min: number) => `Valoarea minimă este ${min}`,
  maxValue: (max: number) => `Valoarea maximă este ${max}`,
  
  // Patient specific
  invalidBirthWeight: 'Greutatea la naștere trebuie să fie între 300g și 6000g',
  invalidGestationalAge: 'Vârsta gestațională trebuie să fie între 22 și 43 săptămâni',
  futureDate: 'Data nu poate fi în viitor',
} as const;

// Medical terminology
export const medicalTerms = {
  chd: 'Cardiopatie congenitală',
  icd10: 'Clasificare internațională a bolilor',
  primary: 'Primar',
  secondary: 'Secundar',
  severity: 'Severitate',
  mild: 'Ușoară',
  moderate: 'Moderată',
  severe: 'Severă',
  critical: 'Critică',
  veryCritical: 'Foarte critică',
  
  
  // Units
  bpm: 'bpm',
  mmHg: 'mmHg',
  percent: '%',
  kg: 'kg',
  cm: 'cm',
  celsius: '°C',
  grams: 'g',
  weeks: 'săpt.',
} as const;

// User levels
export const userLevelLabels = {
  1: 'Utilizator maternitate',
  2: 'Utilizator spital',
  3: 'Administrator',
} as const;

// Table headers
export const tableHeaders = {
  // Patient table
  patientCode: 'Cod',
  patientName: 'Nume pacient',
  birthDate: 'Data nașterii',
  gender: 'Sex',
  hospital: 'Spital',
  actions: 'Acțiuni',
  
  // Diagnosis table
  icd10Code: 'Cod ICD-10',
  description: 'Descriere',
  severity: 'Severitate',
  primary: 'Primar',
  date: 'Data',
  
  // General
  createdAt: 'Creat la',
  updatedAt: 'Actualizat la',
  
  // Procedure table headers
  patient: 'Pacient',
  procedureType: 'Tip procedură',
  procedureName: 'Denumire procedură',
  procedureDate: 'Data',
  outcome30d: 'Rezultat 30 zile',
  performedBy: 'Executat de',
} as const;

// Pagination labels
export const paginationLabels = {
  showing: 'Afișând',
  of: 'din',
  results: 'rezultate',
  page: 'Pagina',
  previous: 'Anterior',
  next: 'Următoarea',
  first: 'Prima',
  last: 'Ultima',
  rowsPerPage: 'Rânduri pe pagină',
} as const;

// Export all labels
export const romanianLabels = {
  navigation: navigationLabels,
  form: formLabels,
  status: statusMessages,
  error: errorMessages,
  medical: medicalTerms,
  userLevel: userLevelLabels,
  table: tableHeaders,
  pagination: paginationLabels,
} as const;